
import { useState, useMemo, useCallback } from 'react';
import { carBrands, carModels, vehicleConditions, brandTiers, fuelTypes } from '../../../mocks/vehicleData';

interface ValuationData {
  marca: string;
  modelo: string;
  año: string;
  combustible: string;
  kilometros: string;
  estado: string;
}

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 35 }, (_, i) => (currentYear - i).toString());

function calculatePrice(data: ValuationData): { min: number; max: number } {
  const age = currentYear - parseInt(data.año);
  const tier = brandTiers[data.marca] || 1;

  let baseMin = 150;
  let baseMax = 400;

  if (tier === 3) {
    baseMin = 600;
    baseMax = 2800;
  } else if (tier === 2) {
    baseMin = 400;
    baseMax = 1800;
  } else {
    baseMin = 150;
    baseMax = 1200;
  }

  // Age factor
  let ageFactor = 1;
  if (age <= 5) ageFactor = 1.8;
  else if (age <= 10) ageFactor = 1.3;
  else if (age <= 15) ageFactor = 0.9;
  else if (age <= 20) ageFactor = 0.6;
  else ageFactor = 0.35;

  // Km factor
  const km = parseInt(data.kilometros) || 0;
  let kmFactor = 1;
  if (km <= 50000) kmFactor = 1.3;
  else if (km <= 100000) kmFactor = 1.1;
  else if (km <= 150000) kmFactor = 0.9;
  else if (km <= 250000) kmFactor = 0.7;
  else kmFactor = 0.5;

  // Condition factor
  const condition = vehicleConditions.find(c => c.value === data.estado);
  const condFactor = condition ? condition.factor : 0.5;

  // Fuel factor
  let fuelFactor = 1;
  if (data.combustible === 'hibrido') fuelFactor = 1.25;
  else if (data.combustible === 'electrico') fuelFactor = 1.4;
  else if (data.combustible === 'diesel' && age > 15) fuelFactor = 0.7;
  else if (data.combustible === 'glp') fuelFactor = 0.85;

  const min = Math.round(baseMin * ageFactor * kmFactor * condFactor * fuelFactor);
  const max = Math.round(baseMax * ageFactor * kmFactor * condFactor * fuelFactor);

  return { min: Math.max(50, min), max: Math.max(100, max) };
}

export default function Calculator() {
  const [step, setStep] = useState(1);
  const [showResult, setShowResult] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [data, setData] = useState<ValuationData>({
    marca: '',
    modelo: '',
    año: '',
    combustible: '',
    kilometros: '',
    estado: ''
  });

  const availableModels = useMemo(() => {
    return data.marca ? (carModels[data.marca] || ['Otro modelo']) : [];
  }, [data.marca]);

  const price = useMemo(() => {
    if (data.marca && data.modelo && data.año && data.combustible && data.kilometros && data.estado) {
      return calculatePrice(data);
    }
    return null;
  }, [data]);

  const updateField = useCallback((field: keyof ValuationData, value: string) => {
    setData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'marca') {
        updated.modelo = '';
      }
      return updated;
    });
  }, []);

  const canProceed = useCallback((s: number) => {
    switch (s) {
      case 1: return data.marca !== '' && data.modelo !== '';
      case 2: return data.año !== '' && data.combustible !== '';
      case 3: return data.kilometros !== '' && data.estado !== '';
      default: return false;
    }
  }, [data]);

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      setIsAnimating(true);
      setTimeout(() => {
        setShowResult(true);
        setIsAnimating(false);
      }, 1800);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleReset = () => {
    setShowResult(false);
    setIsAnimating(false);
    setStep(1);
    setData({ marca: '', modelo: '', año: '', combustible: '', kilometros: '', estado: '' });
  };

  const stepLabels = ['Vehículo', 'Detalles', 'Estado'];

  return (
    <section id="calculadora" className="py-12 sm:py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-orange-100 text-orange-700 rounded-full mb-3 sm:mb-4">
            <i className="ri-calculator-line text-base sm:text-lg"></i>
            <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">CALCULADORA INSTANTÁNEA</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
            ¿Cuánto Vale tu Coche para <strong>Desguace</strong>?
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-2">
            Obtén una estimación al instante. Selecciona los datos de tu vehículo y descubre cuánto te pagamos en Castelldefels.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 overflow-hidden">

            {/* Progress Bar */}
            {!showResult && !isAnimating && (
              <div className="px-4 sm:px-8 pt-6 sm:pt-8">
                <div className="flex items-center justify-between mb-2">
                  {stepLabels.map((label, i) => (
                    <div key={label} className="flex items-center gap-1 sm:gap-2">
                      <div className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full text-xs sm:text-sm font-bold transition-all duration-300 ${
                        step > i + 1 ? 'bg-teal-600 text-white' : step === i + 1 ? 'bg-teal-600 text-white ring-2 sm:ring-4 ring-teal-100' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {step > i + 1 ? <i className="ri-check-line text-base sm:text-lg"></i> : i + 1}
                      </div>
                      <span className={`text-xs sm:text-sm font-medium hidden sm:block ${step >= i + 1 ? 'text-teal-700' : 'text-gray-400'}`}>{label}</span>
                      {i < 2 && (
                        <div className={`hidden sm:block w-8 md:w-16 lg:w-24 h-1 rounded-full mx-1 sm:mx-2 transition-all duration-300 ${step > i + 1 ? 'bg-teal-500' : 'bg-gray-200'}`}></div>
                      )}
                    </div>
                  ))}
                </div>
                {/* Mobile step indicator */}
                <div className="sm:hidden text-center mt-2">
                  <span className="text-xs text-gray-500">Paso {step} de 3: {stepLabels[step - 1]}</span>
                </div>
              </div>
            )}

            {/* Step Content */}
            <div className="p-4 sm:p-8">

              {/* Loading Animation */}
              {isAnimating && (
                <div className="flex flex-col items-center justify-center py-12 sm:py-16">
                  <div className="relative w-20 h-20 sm:w-24 sm:h-24 mb-4 sm:mb-6">
                    <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-teal-500 rounded-full animate-spin" style={{ borderTopColor: 'transparent', borderRightColor: 'transparent' }}></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className="ri-car-line text-2xl sm:text-3xl text-teal-600"></i>
                    </div>
                  </div>
                  <p className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Calculando valoración...</p>
                  <p className="text-gray-500 text-xs sm:text-sm">Analizando datos del mercado en Castelldefels</p>
                </div>
              )}

              {/* Result */}
              {showResult && price && (
                <div className="text-center py-4 sm:py-8">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4 sm:mb-6">
                    <i className="ri-money-euro-circle-line text-3xl sm:text-4xl text-teal-600"></i>
                  </div>
                  <p className="text-xs sm:text-sm font-semibold text-teal-600 uppercase tracking-wide mb-2">Valoración estimada para tu {data.marca} {data.modelo}</p>
                  <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
                    <span className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900">{price.min}€</span>
                    <span className="text-xl sm:text-2xl text-gray-400">-</span>
                    <span className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900">{price.max}€</span>
                  </div>
                  <p className="text-gray-500 mb-6 sm:mb-8 text-xs sm:text-sm px-2">*Precio orientativo. La valoración final puede variar tras inspección presencial.</p>

                  {/* Summary */}
                  <div className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 text-left">
                    <h4 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-xs sm:text-sm uppercase tracking-wide">Resumen del vehículo</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Marca</p>
                        <p className="font-semibold text-gray-900 text-xs sm:text-sm">{data.marca}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Modelo</p>
                        <p className="font-semibold text-gray-900 text-xs sm:text-sm">{data.modelo}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Año</p>
                        <p className="font-semibold text-gray-900 text-xs sm:text-sm">{data.año}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Combustible</p>
                        <p className="font-semibold text-gray-900 text-xs sm:text-sm">{fuelTypes.find(f => f.value === data.combustible)?.label}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Kilómetros</p>
                        <p className="font-semibold text-gray-900 text-xs sm:text-sm">{parseInt(data.kilometros).toLocaleString('es-ES')} km</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Estado</p>
                        <p className="font-semibold text-gray-900 text-xs sm:text-sm">{vehicleConditions.find(c => c.value === data.estado)?.label}</p>
                      </div>
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col gap-3">
                    <a
                      href={`https://wa.me/34932932913?text=${encodeURIComponent(
                        `Hola Desguaces Castelldefels, me interesa vender mi coche. Aquí están los datos:\n\n` +
                        `🚗 Marca: ${data.marca}\n` +
                        `📋 Modelo: ${data.modelo}\n` +
                        `📅 Año: ${data.año}\n` +
                        `⛽ Combustible: ${fuelTypes.find(f => f.value === data.combustible)?.label || data.combustible}\n` +
                        `🔢 Kilómetros: ${parseInt(data.kilometros).toLocaleString('es-ES')} km\n` +
                        `🔧 Estado: ${vehicleConditions.find(c => c.value === data.estado)?.label || data.estado}\n` +
                        `💰 Valoración estimada: ${price.min}€ - ${price.max}€\n\n` +
                        `¿Podrían darme una valoración exacta?`
                      )}`}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="flex items-center justify-center gap-2 px-6 py-3 sm:py-4 bg-green-500 text-white rounded-lg sm:rounded-xl font-semibold hover:bg-green-600 transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base"
                    >
                      <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
                      Enviar por WhatsApp
                    </a>
                    <a
                      href="tel:+34932932913"
                      className="flex items-center justify-center gap-2 px-6 py-3 sm:py-4 bg-orange-500 text-white rounded-lg sm:rounded-xl font-semibold hover:bg-orange-600 transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base"
                    >
                      <i className="ri-phone-line text-lg sm:text-xl"></i>
                      Llamar Ahora
                    </a>
                  </div>
                  <button
                    onClick={handleReset}
                    className="mt-4 text-xs sm:text-sm text-gray-500 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-restart-line mr-1"></i>
                    Calcular otro vehículo
                  </button>
                </div>
              )}

              {/* Step 1: Brand & Model */}
              {!showResult && !isAnimating && step === 1 && (
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                      <i className="ri-car-line mr-1 text-teal-600"></i> Marca del vehículo
                    </label>
                    <div className="relative">
                      <select
                        value={data.marca}
                        onChange={(e) => updateField('marca', e.target.value)}
                        className="w-full px-3 sm:px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm appearance-none bg-white cursor-pointer"
                      >
                        <option value="">Selecciona la marca</option>
                        {carBrands.map(brand => (
                          <option key={brand} value={brand}>{brand}</option>
                        ))}
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                      <i className="ri-steering-line mr-1 text-teal-600"></i> Modelo
                    </label>
                    <div className="relative">
                      <select
                        value={data.modelo}
                        onChange={(e) => updateField('modelo', e.target.value)}
                        disabled={!data.marca}
                        className="w-full px-3 sm:px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm appearance-none bg-white cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">{data.marca ? 'Selecciona el modelo' : 'Primero selecciona la marca'}</option>
                        {availableModels.map(model => (
                          <option key={model} value={model}>{model}</option>
                        ))}
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Year & Fuel */}
              {!showResult && !isAnimating && step === 2 && (
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                      <i className="ri-calendar-line mr-1 text-teal-600"></i> Año de matriculación
                    </label>
                    <div className="relative">
                      <select
                        value={data.año}
                        onChange={(e) => updateField('año', e.target.value)}
                        className="w-full px-3 sm:px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm appearance-none bg-white cursor-pointer"
                      >
                        <option value="">Selecciona el año</option>
                        {years.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                      <i className="ri-gas-station-line mr-1 text-teal-600"></i> Tipo de combustible
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                      {fuelTypes.map(fuel => (
                        <button
                          key={fuel.value}
                          onClick={() => updateField('combustible', fuel.value)}
                          className={`px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                            data.combustible === fuel.value
                              ? 'bg-teal-600 text-white border-teal-600 shadow-md'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-teal-300 hover:bg-teal-50'
                          }`}
                        >
                          {fuel.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Km & Condition */}
              {!showResult && !isAnimating && step === 3 && (
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                      <i className="ri-dashboard-3-line mr-1 text-teal-600"></i> Kilómetros aproximados
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={data.kilometros}
                        onChange={(e) => updateField('kilometros', e.target.value)}
                        placeholder="Ej: 150000"
                        min="0"
                        max="999999"
                        className="w-full px-3 sm:px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                      />
                      <span className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs sm:text-sm pointer-events-none">km</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                      <i className="ri-shield-check-line mr-1 text-teal-600"></i> Estado del vehículo
                    </label>
                    <div className="grid grid-cols-1 gap-2 sm:gap-3">
                      {vehicleConditions.map(cond => (
                        <button
                          key={cond.value}
                          onClick={() => updateField('estado', cond.value)}
                          className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 sm:py-3.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-all cursor-pointer border text-left ${
                            data.estado === cond.value
                              ? 'bg-teal-600 text-white border-teal-600 shadow-md'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-teal-300 hover:bg-teal-50'
                          }`}
                        >
                          <div className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${
                            data.estado === cond.value ? 'bg-white/20' : 'bg-gray-100'
                          }`}>
                            <i className={`${cond.icon} text-base sm:text-lg ${data.estado === cond.value ? 'text-white' : 'text-teal-600'}`}></i>
                          </div>
                          <span className="whitespace-nowrap">{cond.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              {!showResult && !isAnimating && (
                <div className="flex items-center justify-between mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-100">
                  {step > 1 ? (
                    <button
                      onClick={handleBack}
                      className="flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-2.5 sm:py-3 text-gray-600 hover:text-teal-600 font-medium transition-colors cursor-pointer whitespace-nowrap text-sm"
                    >
                      <i className="ri-arrow-left-line"></i>
                      <span className="hidden sm:inline">Anterior</span>
                    </button>
                  ) : <div></div>}
                  <button
                    onClick={handleNext}
                    disabled={!canProceed(step)}
                    className="flex items-center gap-1 sm:gap-2 px-5 sm:px-8 py-2.5 sm:py-3.5 bg-teal-600 text-white rounded-lg sm:rounded-xl font-semibold hover:bg-teal-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-sm sm:text-base"
                  >
                    {step === 3 ? (
                      <>
                        <i className="ri-calculator-line text-base sm:text-lg"></i>
                        <span className="hidden sm:inline">Calcular Precio</span>
                        <span className="sm:hidden">Calcular</span>
                      </>
                    ) : (
                      <>
                        Siguiente
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Trust note */}
          {!showResult && (
            <div className="flex items-center justify-center gap-2 sm:gap-3 mt-4 sm:mt-6 text-gray-500 text-xs sm:text-sm px-4">
              <i className="ri-lock-line text-teal-600"></i>
              <span className="text-center">Tus datos no se almacenan. Valoración 100% anónima y gratuita.</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
